/**
 * Job Progress Tracking — pipeline stage model.
 *
 * Pure logic, extracted from JobProgressPage so it can be unit-tested
 * (same split as `analysis/sdmsTrendMath.ts` + its `.test.ts`).
 */

/** Eight pipeline stages — 12.5% each.  Order matches the Sample Backlog
 *  side-nav + the legacy Houston PW workflow. */
export const STAGES = [
  'Sample Collection',
  'Sample Tracking',
  'Subout Tracking',
  'Sample Preparation',
  'SDMS Result Entry',
  'Data Review',
  'Reporting',
  'Delivery',
] as const;

export type StageStatus = 'Complete' | 'Partial' | 'Pending' | 'Blocked' | 'NotTracked';

/** The per-stage counts a job row carries. These are what let a stage be
 *  judged on fact rather than inferred from the aggregate %. */
export interface StageCounts {
  totalTests:     number;
  pendingCount:   number;
  inReviewCount:  number;
  reportingCount: number;
  reportedCount:  number;
  /** Stage denominator for Subout Tracking: **every test in the job**, not only
   *  the sub-contracted ones (owner spec, 2026-08-10).
   *  **Optional on purpose:** `undefined` means the API did not send it (a
   *  server whose `AI_JobProgressSnapshot` predates the 2026-08-07 columns), and
   *  that must render as "not tracked" — NOT as 0, which would wrongly claim the
   *  job has no subout work at all. */
  suboutTotal?:   number;
  /** Of {@link suboutTotal}: tests needing no subout (already done for this
   *  stage) **plus** sub-contracted tests whose order has come back. */
  suboutDone?:    number;
  /** Stage denominator for Sample Preparation: **every test in the job**. The
   *  name predates the 2026-08-10 whole-job denominator and is kept only to
   *  avoid a breaking schema/DTO rename. Optional for the same reason above. */
  prepRequired?:  number;
  /** Of {@link prepRequired}: tests needing no prep (already done for this
   *  stage) **plus** prep-bearing tests with a prep batch assigned. */
  prepDone?:      number;
}

/**
 * How far a stage has got: `done` of `total`.
 *
 * For Subout Tracking and Sample Preparation the server sends a **whole-job**
 * denominator, counting a test that does not need the stage as already done
 * (owner spec, 2026-08-10). One formula then yields all six required cases:
 *
 * | Job                              | done  | total | Colour |
 * |----------------------------------|-------|-------|--------|
 * | no subout / prep anywhere        | N     | N     | Green  |
 * | some tests need it, none done    | N-k>0 | N     | Orange |
 * | ...those finish                  | N     | N     | Green  |
 * | **every** test needs it, none done | 0   | N     | Grey   |
 * | ...some finish                   | >0    | N     | Orange |
 * | ...all finish                    | N     | N     | Green  |
 *
 * The mixed-job and all-subout cases used to be indistinguishable (both grey)
 * because the denominator counted only the tests needing the stage.
 */
interface StageProgress { done: number; total: number }

/**
 * How far this job has got through one stage, or `undefined` when the payload
 * carries no evidence for it (which renders as `NotTracked`, never a colour).
 *
 * The rule the form exists to show (owner, 2026-08-06): a stage is only "done"
 * once its own work is complete for every sample. Samples *sitting in* a stage
 * waiting are not progress through it — a job whose tests are all at "Pending
 * Reporting" has finished Data Review but has NOT finished Reporting, so
 * Reporting stays grey until a report actually exists.
 */
function stageProgress(
  stage: (typeof STAGES)[number],
  c: StageCounts,
): StageProgress | undefined {
  // The status-derived stages are measured against `totalTests`, which the
  // server builds from CleanSP — a set that EXCLUDES 'Sub Out' samples. A job
  // whose samples are ALL sub-contracted therefore reports totalTests = 0, and
  // those stages genuinely have nothing to measure: `undefined` (NotTracked) is
  // the honest answer, never a 0/0 that would read as Complete.
  const status = c.totalTests > 0;

  switch (stage) {
    // Every job reaching this form has been created and submitted, so both of
    // these are behind it by definition (owner spec, 2026-08-07).
    case 'Sample Collection':
    case 'Sample Tracking':
      return status ? { done: c.totalTests, total: c.totalTests } : undefined;

    // Subout / Prep carry their OWN whole-job denominator from the server, so
    // they still answer when totalTests is 0 — that is exactly the all-subout
    // job, which must read grey rather than vanish into hatching.
    case 'Subout Tracking':
      return c.suboutTotal === undefined || c.suboutDone === undefined
        ? undefined
        : { done: c.suboutDone, total: c.suboutTotal };

    case 'Sample Preparation':
      return c.prepRequired === undefined || c.prepDone === undefined
        ? undefined
        : { done: c.prepDone, total: c.prepRequired };

    // Left the "Pending" bucket -> results have been entered.
    case 'SDMS Result Entry':
      return status ? { done: c.totalTests - c.pendingCount, total: c.totalTests } : undefined;
    // Reached Reporting (or beyond) -> review + approval are behind them.
    case 'Data Review':
      return status ? { done: c.reportingCount + c.reportedCount, total: c.totalTests } : undefined;
    // Actually reported. Waiting in the reporting queue does NOT count.
    case 'Reporting':
      return status ? { done: c.reportedCount, total: c.totalTests } : undefined;
    // Never finished for any row this form can show: the grid is IsComplete = 0
    // and IsComplete means "every report delivered", so a visible job is by
    // definition undelivered.
    case 'Delivery':
      return status ? { done: 0, total: c.totalTests } : undefined;

    default:
      return undefined;
  }
}

/**
 * Does this payload carry evidence for the stage?
 *
 * Derived from {@link stageProgress} rather than a second hand-maintained list,
 * so the two can never drift apart. Subout and Sample Preparation only answer
 * once the server sends the 2026-08-07 snapshot columns; until then they report
 * `NotTracked` rather than a misleading green.
 */
export function isStageTracked(
  stage: (typeof STAGES)[number],
  counts: StageCounts,
): boolean {
  return stageProgress(stage, counts) !== undefined;
}

/**
 * What one pipeline segment should show for a job.
 *
 * **The rule (owner, 2026-08-06):** the form shows a job's CURRENT position. A
 * stage is green only once its own work is finished for every sample; while
 * samples are sitting in a stage waiting, it stays grey. Concretely — if all of
 * a job's samples are at "Pending Reporting", Reporting is GREY, and it turns
 * green only after the report exists.
 *
 * Every segment is judged on {@link stageProgress} counts. Where the payload
 * carries no evidence the segment is `NotTracked` — it is NEVER inferred from
 * the aggregate %.
 *
 * **Why the % may not stand in for a missing stage (2026-08-07).** The weighted
 * score grants a test its stage's full weight the moment it ARRIVES there, so a
 * % alone cannot distinguish "queued at a stage" from "finished with it". Worse,
 * `AI_JobProgress_SPs_v2.sql` line 16 documents Status = 0 as
 * `4/8 = 50% (past Prep, at SDMS Entry)` — i.e. the server ASSERTS Collection,
 * Tracking, Subout and Prep are all behind any pending test, purely because the
 * test exists. That assertion is simply false: job 260807002's two tests have no
 * `PrepMethod` row at all, so prep does not even apply to them, yet slicing the
 * % into eight 12.5% bands painted Sample Preparation orange — 50% being exactly
 * that band's upper bound. The bands are gone; the four previously-unbacked
 * stages now read real columns (Collection/Tracking are done by definition,
 * Subout and Prep have their own counts) or say `NotTracked`.
 *
 * The old exclusive-boundary rule went with the bands, and is not needed: the
 * case it protected — 5 tests at Pending Reporting scoring exactly 87.5% while
 * ReportedCount = 0 (job 260806004) — is now handled directly by the counts,
 * which report `done = 0` and therefore `Pending`.
 *
 * @param progressPct Overall progress 0-100. Retained for call-site compatibility
 *                    and diagnostics; deliberately NOT used to colour anything.
 * @param stageIndex  Zero-based index into {@link STAGES}.
 * @param counts      Per-stage counts from the job row. Omitted → `NotTracked`.
 */
export function stageStatus(
  progressPct: number,
  stageIndex: number,
  counts?: StageCounts,
): StageStatus {
  void progressPct;   // never colours a segment — see the note above

  const stage = STAGES[stageIndex];
  if (!stage || !counts) return 'NotTracked';

  // The `totalTests <= 0` bail-out that used to live here has moved INTO
  // stageProgress, per stage. It was too broad: an all-subout job reports
  // totalTests = 0 (CleanSP strips 'Sub Out' samples), which hatched the whole
  // row — including Subout Tracking, the one stage that job is entirely about.
  const p = stageProgress(stage, counts);
  if (!p) return 'NotTracked';          // stage has no evidence in this payload

  // total === 0 now only means "this job has no tests at all"; done >= total
  // then reads Complete, which is harmless because such a job cannot reach the
  // grid (the status stages return undefined for it first).
  if (p.done >= p.total) return 'Complete';
  return p.done > 0 ? 'Partial' : 'Pending';
}
